import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { CircularProgress, Box, Typography, Alert, keyframes } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

// Keyframe animations for the dots
const dotFlash = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
`;

const AuthCallbackPage: React.FC = () => {
  const { login, setError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        
        // Extract OAuth parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        
        
        // Handle OAuth errors
        if (errorParam) {
          const errorDescription = searchParams.get('error_description') || errorParam;
          setErrorMessage(`Authentication failed: ${errorDescription}`);
          setError(errorDescription);
          
          // Redirect to login page after a delay
          setTimeout(() => {
            navigate('/');
          }, 3000);
          return;
        }
        
        // Validate required parameters
        if (!code || !state) {
          setErrorMessage('Invalid callback - missing OAuth parameters');
          setError('Invalid callback - missing OAuth parameters');
          
          // Redirect to login page after a delay
          setTimeout(() => {
            navigate('/');
          }, 3000);
          return;
        }
        
        
        // Wait a bit for Amplify to process the callback
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if user is authenticated after the callback
        let user;
        try {
          user = await Auth.currentAuthenticatedUser();
        } catch (authError) {
          throw new Error("Authentication verification failed");
        }
        
        if (!user) {
          throw new Error("No user returned from authentication");
        }
        
        // Store the access token in localStorage for axios
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken().getJwtToken();
        localStorage.setItem('aws-token', accessToken);
        
        // For OAuth users, user attributes might be in the ID token payload instead
        const userSession = user.signInUserSession;
        const idToken = userSession?.getIdToken();
        const tokenPayload = idToken?.payload || {};
        
        // Try both user.attributes and token payload
        const userAttributes = user.attributes || {};
        const email = tokenPayload.email || userAttributes.email || userAttributes['cognito:username'] || '';
        const userId = tokenPayload.sub || userAttributes.sub || user.username || 'unknown-id';
        
        // Build full name from available sources
        let userName = '';
        if (tokenPayload.given_name && tokenPayload.family_name) {
          userName = `${tokenPayload.given_name} ${tokenPayload.family_name}`;
        } else if (tokenPayload.name) {
          userName = tokenPayload.name;
        } else if (userAttributes.given_name && userAttributes.family_name) {
          userName = `${userAttributes.given_name} ${userAttributes.family_name}`;
        } else if (userAttributes.name) {
          userName = userAttributes.name;
        } else if (tokenPayload.given_name) {
          userName = tokenPayload.given_name;
        } else if (userAttributes.given_name) {
          userName = userAttributes.given_name;
        } else if (email) {
          userName = email.split('@')[0];
        } else {
          userName = 'User';
        }
        
        
        // Update auth state with user info
        login({
          id: userId,
          email: email,
          name: userName,
        });
        
        
        // Redirect to the dashboard page
        navigate('/dashboard');
        
      } catch (error) {
        let message = 'Authentication failed. Please try again.';
        
        if (error instanceof Error) {
          message = error.message;
          
          // Handle specific Amplify errors
          if (message.includes('not authenticated') || message.includes('User is not authenticated')) {
            message = 'Authentication session expired. Please sign in again.';
          }
        }
        
        setErrorMessage(message);
        setError(message);
        
        // Redirect to login page after a delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, [login, setError, navigate, searchParams]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="50vh"
      >
        <CircularProgress 
          size={60} 
          thickness={3}
          sx={{ 
            mb: 3,
            color: 'primary.main'
          }} 
        />
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2,
            fontWeight: 500,
            color: 'text.primary'
          }}
        >
          Completing authentication
          <Box component="span" sx={{ display: 'inline-flex', ml: 0.5 }}>
            <Box
              component="span"
              sx={{
                animation: `${dotFlash} 1.4s infinite ease-in-out`,
                animationDelay: '0s',
              }}
            >
              .
            </Box>
            <Box
              component="span"
              sx={{
                animation: `${dotFlash} 1.4s infinite ease-in-out`,
                animationDelay: '0.2s',
              }}
            >
              .
            </Box>
            <Box
              component="span"
              sx={{
                animation: `${dotFlash} 1.4s infinite ease-in-out`,
                animationDelay: '0.4s',
              }}
            >
              .
            </Box>
          </Box>
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ maxWidth: '300px', textAlign: 'center' }}
        >
          Please wait while we process your authentication
        </Typography>
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="50vh"
        sx={{ p: 3, textAlign: 'center' }}
      >
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            maxWidth: '500px',
            '& .MuiAlert-message': {
              fontSize: '1rem'
            }
          }}
        >
          {errorMessage}
        </Alert>
        <Typography 
          variant="body1"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          Redirecting to login page in a few seconds...
        </Typography>
        <Typography 
          variant="body2"
          color="text.secondary"
        >
          If you are not redirected automatically, please click{' '}
          <Box 
            component="span" 
            sx={{ 
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => navigate('/')}
          >
            here
          </Box>
        </Typography>
      </Box>
    );
  }

  return null;
};

export default AuthCallbackPage;